/**
 * LessonPlanCard – pinned at the top of the LiveSession transcript when
 * Teacher Mode is on. Shows 3–5 target words/phrases as a progressive
 * checklist that ticks off as the learner uses them.
 *
 * Props:
 *   - plan   : { title, items: [{ target, native, respelling, used, ... }] }
 *   - onPlay : (text) => void   // replay the target via TTS
 */
import { motion } from 'framer-motion';
import { BookOpenText, CheckCircle, Circle, SpeakerHigh } from '@phosphor-icons/react';

export default function LessonPlanCard({ plan, onPlay }) {
  if (!plan?.items?.length) return null;
  const done = plan.items.filter((i) => i.used).length;
  const total = plan.items.length;
  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      className="mx-4 mb-2 mt-3 rounded-2xl border border-[var(--lf-primary)] bg-[#FFF5EE] p-4"
      data-testid="lesson-plan-card"
    >
      <div className="flex items-center gap-2">
        <BookOpenText size={16} weight="duotone" className="text-[var(--lf-primary)]" />
        <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">Today's lesson</div>
        <div className="ml-auto text-xs font-semibold text-[var(--lf-primary)]" data-testid="lesson-plan-progress">
          {done} / {total}
        </div>
      </div>
      {plan.title ? <div className="mt-1 font-bold text-sm">{plan.title}</div> : null}
      <div className="mt-3 space-y-1.5">
        {plan.items.map((it, i) => (
          <div
            key={`${it.target}-${i}`}
            className={`flex items-center gap-2 rounded-xl bg-white px-3 py-2 ${it.used ? 'opacity-70' : ''}`}
            data-testid={`lesson-item-${i}${it.used ? '-done' : ''}`}
          >
            {it.used
              ? <CheckCircle size={18} weight="fill" className="text-[var(--lf-success)] shrink-0" />
              : <Circle size={18} className="text-[var(--lf-text-muted)] shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className={`font-semibold text-sm ${it.used ? 'line-through' : ''}`}>{it.target}</div>
              <div className="text-xs text-[var(--lf-text-muted)] truncate">
                {it.native}{it.respelling ? ` · ${it.respelling}` : ''}
              </div>
            </div>
            <button
              onClick={() => onPlay?.(it.target)}
              className="grid h-8 w-8 place-items-center rounded-full bg-[var(--lf-surface-muted)] text-[var(--lf-primary)] shrink-0"
              title="Hear it"
              data-testid={`lesson-item-play-${i}`}
            >
              <SpeakerHigh size={12} weight="fill" />
            </button>
          </div>
        ))}
      </div>
    </motion.div>
  );
}
